import {
  AssignmentOutlinedIcon,
  BillIcon,
  BookingRequestOutlinedIcon,
  DescriptionOutlinedIcon,
  DirectionsBoatOutlinedIcon,
  PurchaseOrdersIcon,
  RouteIcon,
} from '@freighthub/silhouette-core'
import { CustomerFeature, customerHasFeature } from 'domain/Customer'
import { useBookingsEnabled } from 'features/Booking'
import { useSupplierBookingRequestEnabled } from 'features/BookingRequests'
import { compact } from 'lodash-es'
import { useObserver } from 'mobx-react-lite'
import { FC } from 'react'
import { authorize, hasRoles } from 'service/roles'
import { useAnalytics } from 'service/useAnalytics'
import { useAuth } from 'service/useAuth'
import { useUIStore } from 'service/useUIStore'
import { useI18n } from 'utils/localisation'

import { NavItem } from './NavItem'
import { SidebarNav } from './SidebarNav'

export const menuTranslationMap = {
  shipments: 'menu-shipments',
  bookings: 'menu-bookings',
  directory: 'menu-directory',
  quotes: 'menu-quotes',
  bookingRequests: 'menu-booking_requests',
  reports: 'menu-reports',
  billing: 'menu-billing',
  trackTrace: 'menu-track_trace',
  purchaseOrders: 'menu-purchase_orders',
}

type UseMenuItems = {
  getMenuItems: () => NavItem[]
}

export const useMenuItems = (): UseMenuItems => {
  const authStore = useAuth()
  const uiStore = useUIStore()
  const { translate } = useI18n()
  const analyticsService = useAnalytics()
  const isBookingsEnabled = useBookingsEnabled()
  const isSupplierBookingRequestEnabled = useSupplierBookingRequestEnabled()

  return useObserver(() => {
    const { customer, user } = authStore.state

    const getMenuItems = (): NavItem[] => {
      const menuList: NavItem[] = compact([
        {
          tooltip: translate(menuTranslationMap.shipments),
          icon: DirectionsBoatOutlinedIcon,
          href: '/shipments',
          validPaths: ['/shipments', '/shipment'],
          authorize: authorize.all,
          userlaneId: 'sidebar-link-shipments',
        },
        isBookingsEnabled && {
          tooltip: translate(menuTranslationMap.bookings),
          icon: BookingRequestOutlinedIcon,
          href: '/bookings',
          validPaths: ['/booking', '/bookings'],
          authorize: authorize.all,
          userlaneId: 'sidebar-link-bookings',
        },
        {
          tooltip: translate(menuTranslationMap.quotes),
          icon: AssignmentOutlinedIcon,
          href: '/quotes',
          validPaths: ['/quotes'],
          authorize: authorize.membersOrBilling,
        },
        isSupplierBookingRequestEnabled && {
          tooltip: translate(menuTranslationMap.bookingRequests),
          icon: BookingRequestOutlinedIcon,
          href: '/booking-requests',
          authorize: authorize.all,
          feature: CustomerFeature.SUPPLIER_BOOKING,
        },
        {
          tooltip: translate(menuTranslationMap.reports),
          icon: DescriptionOutlinedIcon,
          href: '/reports',
          authorize: authorize.membersOrBilling,
          userlaneId: 'sidebar-link-reports',
        },
        {
          tooltip: translate(menuTranslationMap.billing),
          icon: BillIcon,
          href: '/invoices',
          authorize: authorize.membersOrBilling,
        },
        {
          tooltip: translate(menuTranslationMap.trackTrace),
          icon: RouteIcon,
          href: '/tracking',
          authorize: authorize.membersOrBilling,
          userlaneId: 'sidebar-link-tracking',
        },
        {
          tooltip: translate(menuTranslationMap.purchaseOrders),
          icon: PurchaseOrdersIcon,
          href: '/purchase-orders?viewBy=purchaseOrderView',
          authorize: authorize.all,
          feature: CustomerFeature.PURCHASE_ORDER_MANAGEMENT,
          showBadge: uiStore.purchaseOrders.pageVersion !== uiStore.pageVersionPOM,
          onClick: () => {
            uiStore.setPurchaseOrdersPageVisited()
            analyticsService.sendEvent('purchaseOrders-pageVisited')
          },
        },
      ])
      return menuList.filter((navItem: NavItem) => {
        const isAuthorized = !navItem.authorize || (!!user && hasRoles(user, navItem.authorize))
        const hasFeatureAccess = !navItem.feature || (!!customer && customerHasFeature(customer, navItem.feature))
        return isAuthorized && hasFeatureAccess && navItem
      })
    }
    return { getMenuItems }
  })
}

export const LeftBar: FC = () => {
  const uiStore = useUIStore()
  const {
    state: { authenticated },
  } = useAuth()

  const onCloseNav = () => {
    uiStore.closeLeftPanel()
  }

  // useObserver is required for uiStore and authStore changes to rerender this component
  return useObserver(() => {
    return authenticated ? <SidebarNav open={uiStore.leftPanel} onClose={onCloseNav} /> : null
  })
}
